import { Badge, Text, Box, Modal, Stack, Divider, Group } from '@mantine/core';
import { StickyNote } from 'lucide-react';
import { GroupBadge } from '../../common';
import { ParticipantEmailPill } from '../components/ParticipantEmailPill';
import { RichTextContent } from '../../common/RichTextEditor/RichTextContent';
import { ENGAGEMENT_RATING_MAP } from '../../../config/engagementRatingConfig';
import { ONE_ON_ONE_STATUS_OPTIONS } from './oneOnOneColumns';
import type { OneOnOneDisplayRow } from './useDataLibraryOneOnOnes';
import { getZoneCode } from '../../../utils/formatters';

/** Status label to badge color; the 1:1 history uses it too. */
export const STATUS_COLORS: Record<string, string> = Object.fromEntries(
  ONE_ON_ONE_STATUS_OPTIONS.map(o => [o.label, o.color]),
);

function stripHtml(html: string): string {
  // A space after each block end, so paragraphs and list items do not run together.
  const spaced = html.replace(/<\/(p|div|li|h[1-6])>|<br\s*\/?>/gi, '$& ');
  const doc = new DOMParser().parseFromString(spaced, 'text/html');
  return doc.body.textContent?.replace(/\s+/g, ' ').trim() || '';
}

// The viewer's own timezone, the same as every other Data Library date.
export function formatDateTime(dateStr: string): string {
  const d = new Date(dateStr);
  const text = d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  return `${text} ${getZoneCode(null, d)}`;
}

export const dash = <Text size="sm" c="dimmed">&mdash;</Text>;

/** Preview of a long text; a click opens the full 1:1 notes. */
function NotesPreviewCell({ text, onOpen }: { text: string | null; onOpen: () => void }) {
  if (!text) return dash;
  return (
    <Box
      component="button"
      type="button"
      onClick={onOpen}
      style={{ cursor: 'pointer', maxWidth: 380, background: 'none', border: 0, padding: 0, textAlign: 'left' }}
    >
      <Group gap={6} wrap="nowrap" align="flex-start">
        <StickyNote size={14} color="#888" style={{ flexShrink: 0, marginTop: 2 }} />
        <Box style={{ minWidth: 0, whiteSpace: 'normal' }}>
          <Text size="sm" lineClamp={2}>{text}</Text>
          <Text size="xs" c="blue" mt={2}>View →</Text>
        </Box>
      </Group>
    </Box>
  );
}

export function OneOnOneNotesModal({ row, onClose }: { row: OneOnOneDisplayRow | null; onClose: () => void }) {
  const sections = row
    ? [
        row.attendance_notes && (
          <Box key="attendance">
            <Text size="xs" fw={600} mb={4}>Coach Notes</Text>
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{row.attendance_notes}</Text>
          </Box>
        ),
        row.session_notes && (
          <Box key="session">
            <Text size="xs" fw={600} mb={4}>Session Notes</Text>
            <Box style={{ fontSize: 14, lineHeight: 1.5 }}>
              <RichTextContent content={row.session_notes} />
            </Box>
          </Box>
        ),
        row.feedback.length > 0 && (
          <Box key="feedback">
            <Text size="xs" fw={600} mb={4}>Coach Feedback</Text>
            <Stack gap="xs">
              {row.feedback.map(f => (
                <Box key={f.label}>
                  <Text size="xs" c="dimmed">{f.label}</Text>
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{f.answer}</Text>
                </Box>
              ))}
            </Stack>
          </Box>
        ),
      ].filter(Boolean)
    : [];

  return (
    <Modal
      opened={!!row}
      onClose={onClose}
      title={row ? `1:1 notes: ${row.participant_name ?? 'Participant'}` : ''}
      size="lg"
      withinPortal
    >
      {row && (
        <Stack gap="md">
          <Text size="xs" c="dimmed">
            {[row.coach_name, row.scheduled_start && formatDateTime(row.scheduled_start), row.status]
              .filter(Boolean)
              .join(' · ')}
          </Text>
          {sections.map((section, i) => (
            <Box key={i}>
              {i > 0 && <Divider mb="md" />}
              {section}
            </Box>
          ))}
        </Stack>
      )}
    </Modal>
  );
}

export function renderOneOnOneCell(
  row: OneOnOneDisplayRow,
  columnId: string,
  openNotes: (row: OneOnOneDisplayRow) => void,
): React.ReactNode {
  switch (columnId) {
    case 'participant_name':
      return row.participant_name ? <Text size="sm" fw={500}>{row.participant_name}</Text> : dash;
    case 'participant_email':
      return row.participant_email && row.participant_id ? (
        <ParticipantEmailPill email={row.participant_email} participantId={row.participant_id} cohortId={row.cohort_id} />
      ) : (
        dash
      );
    case 'group_name':
      return row.group_name ? <GroupBadge groupName={row.group_name} groupId={row.group_id} /> : dash;
    case 'scheduled_start':
      return row.scheduled_start ? (
        <Text size="sm" c="dimmed">
          {formatDateTime(row.scheduled_start)}
        </Text>
      ) : (
        dash
      );
    case 'session_number':
      return row.session_number != null ? <Text size="sm">{row.session_number}</Text> : dash;
    case 'misses':
      return row.misses ? <Text size="sm">{row.misses}</Text> : dash;
    case 'status':
      return (
        <Badge size="sm" variant="light" color={STATUS_COLORS[row.status] ?? 'gray'} styles={{ label: { textTransform: 'none' } }}>
          {row.status}
        </Badge>
      );
    case 'attendance_notes':
      return <NotesPreviewCell text={row.attendance_notes} onOpen={() => openNotes(row)} />;
    case 'session_notes':
      return <NotesPreviewCell text={row.session_notes && stripHtml(row.session_notes)} onOpen={() => openNotes(row)} />;
    case 'feedback':
      return (
        <NotesPreviewCell
          text={row.feedback.length > 0 ? row.feedback.map(f => `${f.label}: ${f.answer}`).join(' · ') : null}
          onOpen={() => openNotes(row)}
        />
      );
    case 'engagement_rating': {
      const cfg = row.engagement_rating != null ? ENGAGEMENT_RATING_MAP.get(row.engagement_rating) : undefined;
      if (!cfg) return dash;
      return (
        <Badge size="sm" variant="light" color={cfg.color} styles={{ label: { textTransform: 'none' } }}>
          {cfg.value} · {cfg.label}
        </Badge>
      );
    }
    default: {
      const value = (row as unknown as Record<string, unknown>)[columnId];
      return value == null || value === '' ? dash : <Text size="sm">{String(value)}</Text>;
    }
  }
}
